import { TicketCard } from "@jugl-web/domain-resources/tickets/components/TicketCard";
import { useTicketList } from "@jugl-web/domain-resources/tickets/hooks/useTicketList";
import { DataLoadingWrapper } from "@jugl-web/ui-kit";
import { useTranslations } from "@jugl-web/utils";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { FC, useEffect, useMemo } from "react";
import { useInView } from "react-intersection-observer";
import { useSubscribeToTicketListMutations } from "@jugl-web/domain-resources/tickets/hooks/useSubscribeToTicketListMutations";
import { CustomerInfoCollapseSection } from "../CustomerInfoCollapseSection";
import { ReactComponent as TicketsIcon } from "./assets/tickets.svg";

export type CustomerOtherTicketsSectionProps = {
  ticketIdsToExclude: string[];
  contactId: string;
  onTicketClick: (ticketId: string) => void;
};

export const CustomerOtherTicketsSection: FC<
  CustomerOtherTicketsSectionProps
> = ({ ticketIdsToExclude, contactId, onTicketClick }) => {
  const { entityId } = useEntitySelectedProvider();

  const { ref: inViewRef, inView } = useInView();

  const { t } = useTranslations();

  const {
    tickets,
    isLoading,
    isLoadingMore,
    isError,
    isLoadingMoreError,
    hasMore,
    retry,
    loadMore,
    setTickets,
    setTotalCount,
  } = useTicketList({
    entityId,
    contactId,
    skip: !contactId,
    sort: "desc",
    pageSize: 10,
  });

  const filteredTickets = useMemo(
    () => tickets.filter((ticket) => !ticketIdsToExclude.includes(ticket.id)),
    [tickets, ticketIdsToExclude]
  );

  useSubscribeToTicketListMutations({
    entityId,
    sort: "desc",
    hasMore,
    contactId,
    setTickets,
    setTotalCount,
  });

  // Load more tickets when the sentinel is in view
  useEffect(() => {
    if (inView && hasMore && !isLoadingMore) {
      loadMore();
    }
  }, [hasMore, inView, isLoadingMore, loadMore]);

  return (
    <>
      <div className="my-7 h-px w-full shrink-0 bg-[#F2F2F2]" />
      <CustomerInfoCollapseSection
        title={t({
          id: "client-support-page.other-tickets",
          defaultMessage: "Other Tickets",
        })}
        icon={<TicketsIcon />}
      >
        <DataLoadingWrapper
          isError={isError}
          isLoading={isLoading}
          onRetry={retry}
          isLoadingMore={isLoadingMore}
          isLoadingMoreError={isLoadingMoreError}
        >
          <div className="flex flex-col gap-4">
            {filteredTickets.map((ticket) => (
              <TicketCard
                key={ticket.id}
                ticket={ticket}
                withContact={false}
                onClick={() => onTicketClick(ticket.id)}
              />
            ))}
            {filteredTickets.length === 0 && (
              <span className="font-secondary text-[13px] leading-[140%] tracking-[0.13px] text-dark-600">
                No other tickets found for this customer ℹ️
              </span>
            )}
          </div>
          {hasMore && !isLoading && !isLoadingMore && <div ref={inViewRef} />}
        </DataLoadingWrapper>
      </CustomerInfoCollapseSection>
    </>
  );
};
